// 06/2026: Dialog zum Bearbeiten/Löschen einer manuellen Kalender-Sperre.
// Der Grund-Text ist jederzeit änderbar; Sperre kann aufgehoben werden.
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Pause, Lock, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PRESETS = ['Wartung', 'Ferien', 'Krankheit', 'Sitzung'];

export const BlockEditDialog = ({ open, block, type, onClose, onSaved }) => {
  const [reason, setReason] = useState(block?.reason || '');
  const [startTime, setStartTime] = useState(block?.start_time || '');
  const [endTime, setEndTime] = useState(block?.end_time || '');
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    setReason(block?.reason || '');
    setStartTime(block?.start_time || '');
    setEndTime(block?.end_time || '');
  }, [block]);

  if (!block) return null;
  const isStudio = type === 'studio';
  const auth = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
  const base = isStudio
    ? `${BACKEND_URL}/api/studios/${block.studio_id}/blocks/${block.id}`
    : `${BACKEND_URL}/api/trainers/blocks/${block.id}`;

  const handleSave = async () => {
    if (!block.all_day && startTime >= endTime) {
      toast.error('Startzeit muss vor der Endzeit liegen');
      return;
    }
    setBusy(true);
    try {
      const body = { reason };
      if (!block.all_day) {
        body.start_time = startTime;
        body.end_time = endTime;
      }
      await axios.patch(base, body, auth);
      toast.success('Sperre aktualisiert');
      onSaved && (await onSaved());
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await axios.delete(base, auth);
      toast.success('Sperre aufgehoben');
      onSaved && (await onSaved());
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Aufheben fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="block-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isStudio ? <Lock size={18} className="text-amber-600" /> : <Pause size={18} className="text-rose-600" />}
            {isStudio ? 'Studio-Sperre bearbeiten' : 'Sperre bearbeiten'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {block.all_day ? (
            <p className="text-sm text-gray-500">Ganztägig</p>
          ) : (
            <div className="flex items-end gap-3">
              <div className="space-y-1">
                <Label htmlFor="block-start">Von</Label>
                <Input
                  id="block-start"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  data-testid="block-edit-start-input"
                />
              </div>
              <span className="pb-2 text-gray-400">–</span>
              <div className="space-y-1">
                <Label htmlFor="block-end">Bis</Label>
                <Input
                  id="block-end"
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  data-testid="block-edit-end-input"
                />
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setReason(p)}
                className="px-3 py-1 rounded-full border text-sm hover:bg-gray-100"
                data-testid={`block-edit-preset-${p}`}
              >
                {p}
              </button>
            ))}
          </div>
          <div className="space-y-1">
            <Label htmlFor="block-reason">Grund (jederzeit änderbar)</Label>
            <Input
              id="block-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="z.B. Michi Frei - Alex arbeitet"
              data-testid="block-edit-reason-input"
              autoFocus
            />
          </div>
        </div>

        <DialogFooter className="flex sm:justify-between gap-2">
          <Button variant="destructive" onClick={handleDelete} disabled={busy} data-testid="block-edit-delete">
            <Trash2 size={15} className="mr-1" /> Sperre aufheben
          </Button>
          <Button onClick={handleSave} disabled={busy} data-testid="block-edit-save">
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BlockEditDialog;
